<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import StatusChip from './StatusChip.vue';
import PlayerTabs from './PlayerTabs.vue';
import ProfileNav from './ProfileNav.vue';
import SectorPass from './sectors/SectorPass.vue';
import SectorDocuments from './sectors/SectorDocuments.vue';
import SectorTravel from './sectors/SectorTravel.vue';
import SectorMaterial from './sectors/SectorMaterial.vue';
import SectorTransfer from './sectors/SectorTransfer.vue';
import SectorX11 from './sectors/SectorX11.vue';
import SectorLocked from './sectors/SectorLocked.vue';
import SectorPlaceholder from './sectors/SectorPlaceholder.vue';
import { usePlayers } from '../composables/usePlayers.js';
import { useAuth } from '../composables/useAuth.js';
import { useConfirm } from '../composables/useConfirm.js';
import { useUnsavedChanges } from '../composables/useUnsavedChanges.js';
import { usePlayerEvents } from '../composables/usePlayerEvents.js';
import { useFileActions } from '../composables/useFileActions.js';
import { useProfileNav } from '../composables/useProfileNav.js';
import { peekUploads, clearUploads } from '../composables/useUploadTracker.js';
import { fetchTransferLogoPaths, useTransfers } from '../composables/useTransfers.js';
import { fetchRegisterLogoPaths, useFootballClubs } from '../composables/useFootballClubs.js';
import { playerClub } from '../domain/footballClub.js';
import { saveWithCleanup, collectProtectedPaths } from '../domain/fileCleanup.js';
import { fullName } from '../domain/format.js';
import { birthYearOf } from '../domain/birthYear.js';
import { tabsForStatus } from '../domain/playerTabs.js';

const { t } = useI18n();
const { openPlayer, requestedTab, close, save, players } = usePlayers();
const { can, isVisible, canDeleteFiles } = useAuth();
const { confirm } = useConfirm();
const { byId: clubById } = useFootballClubs();
const { setDirty } = useUnsavedChanges();
const { announceChanges } = usePlayerEvents();
const { remove: removeStoredFile } = useFileActions();

const activeTab = ref('pass');
const saving = ref(false);
const saved = ref(false);
const saveError = ref(null);

/* Letzter gespeicherter Stand des gerade offenen Spielers, als JSON-Text —
   dagegen wird verglichen, ob es etwas zu speichern gibt. Kein Entwurf/keine
   Kopie: Die Sektoren ändern weiterhin direkt das echte Spielerobjekt (wie
   bisher), das hier nur als Text-Schnappschuss verglichen wird. Reicht für
   Größe M dieser Aufgabe; ein echtes Entwurfsobjekt je Sektor wäre ein
   eigener, größerer Umbau. */
const snapshot = ref(null);

const hasChanges = computed(() => {
  const p = openPlayer.value;
  if (!p || snapshot.value == null) return false;
  return JSON.stringify(p) !== snapshot.value;
});

watch(hasChanges, (dirty) => {
  setDirty('player-panel', dirty);
  /* Neue Änderung nach einer erfolgreichen Speicherung: Bestätigung wieder
     ausblenden, sonst stünde "Gespeichert" neben ungespeicherten Feldern. */
  if (dirty) saved.value = false;
});

/* Gibt es Einträge in player_transfers? Entscheidet mit über den Reiter Transfer Info
   (domain/playerTabs.js). Dieselbe Liste und dieselbe Abfrage wie in TransferHistory.vue
   (composables/useTransfers.js): Das Panel fragt beim Öffnen eines Spielers frisch ab, die
   Historie benutzt danach die geladene Liste. Wird der erste Eintrag angelegt oder der letzte
   gelöscht, ändert sich die Liste sofort — der Reiter erscheint bzw. verschwindet. Bis die
   Antwort da ist, fehlt der Reiter bei Kader-Spielern kurz. */
const { items: transferItems } = useTransfers(() => openPlayer.value?.id, { refresh: true });
const hasTransfers = computed(() => transferItems.value.length > 0);

/* Reiter je Status, siehe domain/playerTabs.js: Player Info steht in jedem Status an erster
   Stelle (auch bei transferierten Spielern), Transfer Info — falls es ihn gibt — direkt dahinter. */
const tabs = computed(() => {
  const p = openPlayer.value;
  if (!p) return [];
  const all = tabsForStatus(p.status, { hasTransfers: hasTransfers.value }).map((t) => ({
    ...t,
    allowed: can(t.area),
  }));

  /* Gesperrte Reiter bleiben sichtbar und erklären sich beim Anklicken.
     Was ganz verschwindet, regelt isVisible() — siehe domain/permissions.js. */
  return all.filter((t) => isVisible(t.area));
});

/* Verschwindet der aktive Reiter, springt das Panel auf den ersten sichtbaren Reiter (das ist
   Player Info) — sonst bliebe ein leerer Bereich stehen. Fälle: der letzte Transfer-Eintrag wurde
   gelöscht, oder der Reiter X11 Motion ist für die Rolle nicht sichtbar (nur admin, board, data;
   z. B. nach einem Rollen- oder Kontowechsel, während X11 offen war). */
/* Beim Wechsel zum Nachbarprofil (useProfileNav) bleibt der gewählte Reiter, wenn es ihn beim neuen Spieler gibt
   (z. B. durch das Material mehrerer Spieler wischen). Der Reiter "Transfer Info" hängt an einer Abfrage je
   Spieler und fehlt beim neuen Spieler kurz: Darum wird der Wunsch gemerkt (carryTab) und beim nächsten
   Reiter-Update eingelöst, statt sofort auf Player Info zu springen. Ein Klick auf einen Reiter oder eine
   Benachrichtigung löscht den Wunsch, sonst verfällt er nach 3 s (onSwitch unten); Schließen löscht ihn auch. */
const carryTab = ref(null);
let carryTimer = 0;
onBeforeUnmount(() => clearTimeout(carryTimer));
/* Panel zu: kein Reiter-Wunsch mitnehmen (nächstes Öffnen aus der Liste startet auf Player Info) */
watch(() => openPlayer.value === null, (closed) => { if (closed) carryTab.value = null; });

watch(tabs, (list) => {
  if (!list.length) return;
  if (carryTab.value && list.some((t) => t.key === carryTab.value)) {
    activeTab.value = carryTab.value;
    return;
  }
  if (carryTab.value) return; // Reiter kommt eventuell noch (Abfrage läuft): vorerst stehen lassen
  if (list.some((t) => t.key === activeTab.value)) return;
  activeTab.value = list[0].key;
});

function selectTab(key) {
  carryTab.value = null;
  activeTab.value = key;
}

const activeTabAllowed = computed(
  () => tabs.value.find((t) => t.key === activeTab.value)?.allowed !== false
);

/* Die Reiterleiste samt gleitender Kapsel steht in PlayerTabs.vue (eine Zeile, waagerecht scrollbar). */

const activeArea = computed(
  () => tabs.value.find((t) => t.key === activeTab.value)?.area || 'players'
);

/* Beim Öffnen eines anderen Spielers auf den ersten Reiter (Player Info) springen und
   einen frischen Schnappschuss für den Speichern-Knopf nehmen — außer eine
   Benachrichtigung wünscht sich einen bestimmten Reiter (siehe
   usePlayers.js, requestedTab). Ein Wunsch zählt auch, wenn derselbe Spieler
   schon offen ist (z. B. zweite Meldung zum selben Spieler antippen) —
   deshalb ein gemeinsamer Watcher statt zwei getrennter: openPlayer allein
   würde dabei nicht neu feuern. Ungültige oder fehlende Wünsche fallen auf
   den ersten Reiter zurück, aber nur beim tatsächlichen Spielerwechsel —
   sonst würde jeder Klick auf die Glocke den offenen Spieler auf den ersten
   Reiter zurückspringen lassen.

   "Spielerwechsel" heißt: andere `id`, nicht andere Objekt-Referenz.
   usePlayers.js' save() ersetzt den Eintrag in players[] durch die vom
   Server zurückgegebene Fassung (players.value[i] = saved) — openPlayer
   bekommt dadurch nach jedem Speichern eine NEUE Referenz auf denselben
   Spieler. Über die `id` verglichen bleibt das ein "kein Wechsel", der Reiter
   springt beim Speichern also nicht zurück. Der Schnappschuss nach dem
   Speichern wird stattdessen gezielt in onSave() genommen. */
watch([openPlayer, requestedTab], ([p, wanted], prev) => {
  if (!p) return;
  const changedPlayer = !prev || prev[0]?.id !== p.id;
  if (changedPlayer) {
    snapshot.value = JSON.stringify(p);
    saved.value = false;
    saveError.value = null;
  }
  if (wanted == null && !changedPlayer) return;
  requestedTab.value = null;
  const hasWanted = wanted && tabs.value.some((t) => t.key === wanted);
  if (hasWanted) {
    carryTab.value = null;
    activeTab.value = wanted;
  } else if (changedPlayer && carryTab.value) {
    /* Wechsel zum Nachbarn: Reiter behalten, falls vorhanden; sonst Player Info (und weiter versuchen, siehe oben) */
    if (tabs.value.some((t) => t.key === carryTab.value)) {
      activeTab.value = carryTab.value;
    } else if (!tabs.value.some((t) => t.key === activeTab.value)) {
      activeTab.value = tabs.value[0]?.key || 'pass';
    }
  } else if (changedPlayer) {
    activeTab.value = tabs.value[0]?.key || 'pass';
  }
});

const isOpen = computed(() => !!openPlayer.value);

const meta = computed(() => {
  const p = openPlayer.value;
  if (!p) return '';
  return p.pos || '';
});

/* Explizit Speichern statt still beim Schließen — Entscheidung Bastian,
   TODO.md "Speichern-Knopf statt stillem Speichern": Wer etwas ändert, soll
   sehen, dass (und wann) es gespeichert wurde, statt sich beim Schließen
   darauf zu verlassen. save() liefert die vom Server zurückgegebene Fassung
   zurück — der neue Schnappschuss stammt bewusst daraus, nicht aus `p`
   selbst, falls der Server etwas normalisiert hat. */
async function onSave() {
  const p = openPlayer.value;
  if (!p || !can('players', 'write') || !hasChanges.value) return;
  saving.value = true;
  saveError.value = null;
  try {
    /* Vorher-Stand für die Benachrichtigungen, BEVOR der Schnappschuss
       überschrieben wird. Ausgelöst wird erst nach erfolgreichem Speichern;
       bewusst nicht abgewartet — die Meldung soll das Speichern nicht bremsen
       und ihr Scheitern es nicht als gescheitert erscheinen lassen. */
    const before = JSON.parse(snapshot.value);
    /* Nach ERFOLGREICHEM Speichern werden Foto- und Logo-Dateien gelöscht, auf die
       weder der gespeicherte Stand noch ein anderer Spieler mehr zeigt
       (domain/fileCleanup.js). Scheitert save(), geschieht nichts. Nur admin darf
       löschen (Speicherregel player_files_delete); für alle anderen wird still
       übersprungen. Das Löschen läuft im Hintergrund und bremst "Gespeichert"
       nicht. */
    const uploads = peekUploads(p.id);
    /* Geschützt sind (1) die Logos der Transferhistorie (player_transfers) und (2) ALLE Logos des
       Klubregisters (football_clubs). Frisch aus der Datenbank gelesen; gelingt eins von beiden nicht,
       wird gar nicht aufgeräumt (fail closed). */
    let canDelete = canDeleteFiles();
    let keep = [];
    if (canDelete) {
      const protectedPaths = await collectProtectedPaths([() => fetchTransferLogoPaths(p.id), fetchRegisterLogoPaths]);
      if (protectedPaths === null) canDelete = false;
      else keep = protectedPaths;
    }
    const { result } = await saveWithCleanup({
      save,
      player: p,
      before,
      others: players.value.filter((o) => o.id !== p.id),
      uploaded: uploads,
      keep,
      canDelete,
      remove: (path) => removeStoredFile({ path }),
    });
    clearUploads(p.id);
    snapshot.value = JSON.stringify(result);
    saved.value = true;
    announceChanges(before, result);
  } catch (e) {
    saveError.value = e.message || 'saveFailed';
  } finally {
    saving.value = false;
  }
}

/* Schließen speichert nicht mehr automatisch. Gibt es ungespeicherte
   Änderungen, fragt eine Rückfrage nach, bevor sie verworfen werden —
   derselbe Bestätigungsdialog wie bei Löschungen (useConfirm.js). Bei
   Bestätigung wird das Spielerobjekt tatsächlich auf den Schnappschuss
   zurückgesetzt (nicht nur das Panel geschlossen): Die Liste behält
   denselben Spieler ohne Neuladen im Speicher — ohne das Zurücksetzen stünde
   beim erneuten Öffnen derselbe "verworfene" Stand wieder da. Ohne
   Bestätigung bleibt das Panel offen. */
async function confirmDiscard() {
  const p = openPlayer.value;
  if (hasChanges.value && p) {
    const ok = await confirm({
      title: t('confirm.discardChangesTitle'),
      message: t('confirm.discardChanges'),
    });
    if (!ok) return false;
    Object.assign(p, JSON.parse(snapshot.value));
  }
  return true;
}

async function requestClose() {
  if (await confirmDiscard()) close();
}

/* Wechsel zum vorherigen/nächsten Spieler (Pfeile, Tastatur, Wischen): dieselbe Rückfrage wie beim Schließen,
   wenn ungespeicherte Änderungen offen sind. Logik: composables/useProfileNav.js */
const bodyEl = ref(null);
const nameEl = ref(null);
const nav = useProfileNav({
  bodyEl,
  nameEl,
  beforeLeave: confirmDiscard,
  busy: () => saving.value,
  onSwitch: () => {
    carryTab.value = activeTab.value;
    /* Wunsch verfällt nach kurzer Zeit (Abfrage der Transfers ist dann längst da), damit er nie hängen bleibt */
    clearTimeout(carryTimer);
    carryTimer = setTimeout(() => { carryTab.value = null; }, 3000);
  },
});
/* Kopf (Name, Status) und Inhalt (Karte unter den Reitern) bewegen sich mit demselben Stil als eine Karte. Fest
   bleiben die Reiterleiste, die Leiste mit Pfeilen und Zähler und der Schließen-Knopf. */
const cardStyle = nav.cardStyle;
</script>

<template>
  <div class="overlay" :class="{ open: isOpen }" @click="requestClose"></div>

  <aside
    class="panel"
    :class="{ open: isOpen }"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('tabs.playerInfo')"
  >
    <template v-if="openPlayer">
      <div class="panel-head">
        <div class="head-text">
         <div class="head-slide" :style="cardStyle">
          <div class="name-line">
            <h2 ref="nameEl" tabindex="-1">{{ fullName(openPlayer) }}</h2>
          </div>
          <!-- Status-Chip links, Positionszeile daneben (bei Platzmangel umbrechend). Transferierte ohne Chip (F20) -->
          <div class="status-line">
            <StatusChip v-if="openPlayer.status !== 'transferred'" :status="openPlayer.status" />
            <div class="meta">
              <span class="meta-pos">{{ meta }}</span>
              <template v-if="birthYearOf(openPlayer)">
                · {{ $t('player.born', { year: birthYearOf(openPlayer) }) }}
              </template>
              <template v-if="openPlayer.status === 'transferred' && playerClub(clubById, openPlayer).name">
                · {{ $t('player.nowAt').toLowerCase() }} {{ playerClub(clubById, openPlayer).name }}
              </template>
            </div>
          </div>
         </div>
        </div>
        <button class="close-btn" :disabled="saving" @click="requestClose">
          {{ $t('common.close') }} ✕
        </button>
      </div>

      <!-- Nur mit Listenkontext (aus der Liste geöffnet): vorheriger/nächster Spieler -->
      <ProfileNav
        :position="nav.position.value"
        :can-prev="nav.canPrev.value"
        :can-next="nav.canNext.value"
        :skipped="nav.skipped.value"
        :disabled="saving"
        @step="nav.step"
      />

      <div
        ref="bodyEl"
        class="panel-body"
        :class="[`tab-${activeTab}`, { swipeable: nav.position.value, sliding: nav.phase.value !== 'idle' }]"
        v-bind="nav.handlers"
      >
        <!-- Reiter: fest, gehören nicht zur bewegten Karte (siehe card-slide unten) -->
        <PlayerTabs :tabs="tabs" :active-key="activeTab" @select="selectTab" />

        <!-- Die Karte: nur dieser Teil gleitet beim Wechsel mit dem Kopf; die Reiter oben stehen fest -->
        <div class="card-slide" :style="cardStyle">
        <SectorLocked v-if="!activeTabAllowed" :area="activeArea" />
        <SectorPass
          v-else-if="activeTab === 'pass'"
          :player="openPlayer"
          @show-tab="selectTab"
        />
        <SectorDocuments v-else-if="activeTab === 'documents'" :player="openPlayer" />
        <SectorTravel v-else-if="activeTab === 'travel'" :player="openPlayer" />
        <SectorMaterial v-else-if="activeTab === 'material'" :player="openPlayer" />
        <SectorTransfer v-else-if="activeTab === 'transfer'" :player="openPlayer" />
        <SectorX11 v-else-if="activeTab === 'x11'" />
        <SectorPlaceholder v-else :tab="activeTab" />
        </div>
      </div>

      <!-- Speichern-Knopf statt stillem Speichern: aktiv, sobald sich etwas
           geändert hat, danach wieder ausgegraut. Nur für Rollen mit
           Schreibrecht auf Spielerdaten — ohne das gäbe es ohnehin nichts zu
           speichern. -->
      <div v-if="can('players', 'write')" class="panel-footer">
        <span v-if="saveError" class="save-msg error">{{ $t(`errors.${saveError}`) }}</span>
        <span v-else-if="saved" class="save-msg ok">{{ $t('player.saved') }}</span>
        <button
          type="button"
          class="btn save-btn"
          :class="{ dirty: hasChanges && !saving }"
          :disabled="saving"
          :aria-disabled="!hasChanges || saving"
          @click="onSave"
        >
          {{ saving ? $t('common.loading') : $t('common.save') }}
        </button>
      </div>
    </template>
  </aside>
</template>

<style scoped>
/* Waagerechte Wischgeste gehört dem Profil (Spielerwechsel), senkrecht scrollt der Browser. Inhalte mit
   eigenem waagerechten Scrollen sind davon ausgenommen (domain/profileNav.js isSwipeBlocked). */
.panel-body.swipeable {
  touch-action: pan-y;
}
/* Kopftext liegt in einem eigenen Fenster: Beim Wechsel gleitet er darin mit der Karte, ohne unter den
   Schließen-Knopf oder aus dem Panel zu laufen. Der kleine Innenabstand lässt den Fokusring des Namens stehen. */
.head-text {
  overflow: hidden;
  padding: 3px;
  margin: -3px;
}
.head-slide,
.card-slide {
  will-change: transform, opacity;
}
.card-slide {
  min-width: 0;
}
/* Während der Bewegung läuft die Karte seitlich aus dem Inhalt heraus: Das darf keine waagerechte Bildlaufleiste
   erzeugen. Nur dann, damit breite Inhalte im Ruhezustand ihr Verhalten behalten. */
.panel-body.sliding {
  overflow-x: hidden;
}
.panel-head h2:focus {
  outline: none;
}
.panel-head h2:focus-visible {
  outline: 2px solid var(--green);
  outline-offset: 2px;
}
.close-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

/* Fläche, Abstände und Speichern-Knopf des Fußes: styles/profile.css */
.panel-footer {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
.save-msg {
  flex: 1;
  min-width: 0;
  font-size: 0.78rem;
  line-height: 1.5;
}
.save-msg.ok {
  color: var(--green);
}
.save-msg.error {
  color: var(--red);
}
</style>
